import { Button } from '@mantine/core'

import { useAuthenticatedGraphMutation } from '@/hooks/use-authenticated-query'
import { GenerateDocumentLinkGql } from '@/graphql/mutations/resources/generate-document-link.graphql'
import { useState } from 'react'
import { IconDownload } from '@tabler/icons-react'
import { showErrorNotification } from '@shared/helpers/notifications/notifications'

interface DocumentViewButtonProps {
  relativeUrl: string
}

/**
 * A React component that renders a button for viewing a document.
 * When clicked, it fetches a document link using a GraphQL query and opens the document in a new tab.
 * If the document cannot be fetched, an error notification is displayed.
 *
 * @param {DocumentViewButtonProps} props - The props for the component.
 * @param {string} props.relativeUrl - The relative URL of the document to be fetched.
 *
 * @returns {JSX.Element} A button that allows the user to view a document.
 *
 * @remarks
 * This component would be much simpler if the endpoint was a GraphQL mutation, that
 * creates a one time link to the document. This would allow us to use callbacks
 * and simplify the logic.
 */
export const DocumentViewButton = ({ relativeUrl }: DocumentViewButtonProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const errorMessage = 'Unable to download document. Please try again later.'

  // Mutation to generate a new document link
  const createDocumentLinkMutation = useAuthenticatedGraphMutation({
    mutationKey: ['documentLink', relativeUrl],
    gql: GenerateDocumentLinkGql,
    gqlKey: 'documentLink',
    onSuccess: (mutation) => {
      const responseData = mutation.documentLink
      if (responseData?.documentLink) {
        window.open(responseData.documentLink, '_blank')
      } else if (responseData?.exceptionMessage || responseData?.fileWasNotFoundError) {
        showErrorNotification(errorMessage)
      }
    },
    onError: () => {
      showErrorNotification(errorMessage)
    },
  })

  const handleDownloadDocument = () => {
    setIsLoading(true)
    createDocumentLinkMutation.mutate({ relativeUrl }, { onSettled: () => setIsLoading(false) })
  }

  return (
    <Button
      size='xs'
      variant='outline'
      onClick={() => handleDownloadDocument()}
      leftSection={<IconDownload />}
      loading={isLoading}
    >
      View
    </Button>
  )
}
